'use client'

import useSwr from 'swr'
import axios from 'axios'
import { TAP_READER_URL } from '@/utils/constants'
import useTokenPrices from '@/utils/hooks/useTokenPrices'

interface TapToken {
	ticker: string
	overall_balance: number
	transferable_balance: string | number
	available_balance: number
	collection: { slug: string; floor_price_per?: number; icon?: string } | { slug: string }
	type: string
}

interface RestRow {
	ticker: string
	overallBalance: string
	transferableBalance: string
}

interface RestResponse {
	data?: { total: number; list: RestRow[] }
}

const blockFetcher = async (): Promise<number | null> => {
	const { data } = await axios.get<{ result: number | null }>(
		`${TAP_READER_URL}/getCurrentBlock`
	)
	return typeof data?.result === 'number' ? data.result : null
}

const balanceFetcher = async ([, address, block]: [string, string, number | null]) => {
	const params = block != null ? `?offset=0&max=500&block=${block}` : '?offset=0&max=500'
	const { data } = await axios.get<RestResponse>(
		`${TAP_READER_URL}/getAccountTokensBalance/${address}${params}`
	)
	return data?.data?.list ?? []
}

const toBalance = (raw: string, ticker: string) => {
	// API returns "" (empty string) when an address has no transferable amount,
	// which would parseInt to NaN and poison every downstream balance check.
	if (!raw) return 0
	const parsed = parseInt(raw, 10)
	if (Number.isNaN(parsed)) return 0
	return parsed / (ticker.toLowerCase().startsWith('dmt') ? 1 : 1e18)
}

export default function useTapBalanceRest(address: string): TapToken[] {
	const { data: tokenPrices } = useTokenPrices()
	const { data: block } = useSwr(
		address ? 'tap-reader/currentBlock' : null,
		blockFetcher,
		{ refreshInterval: 60_000, dedupingInterval: 30_000 }
	)
	const { data: rows } = useSwr(
		address ? ['tap-reader/balances', address, block ?? null] : null,
		balanceFetcher,
		{ revalidateOnFocus: false }
	)

	if (!rows) return []

	return rows
		.map((row) => {
			const tickerUpper = row.ticker.toUpperCase()
			const overall = toBalance(row.overallBalance, row.ticker)
			const transferable = toBalance(row.transferableBalance, row.ticker)
			return {
				ticker: tickerUpper,
				overall_balance: overall,
				transferable_balance: transferable,
				available_balance: overall - transferable,
				collection: (tokenPrices || []).find((e) => e.slug === `tap-${tickerUpper}`) || {
					slug: `tap-${tickerUpper}`
				},
				type: 'tap'
			}
		})
		.filter((t) => t.overall_balance > 0)
}
